import type { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from "axios"
import axios from "axios"

import { refreshToken as callRefreshToken } from "@/api/refresh/api"
import customErrorCode from "@/enums/customErrorCode"
import { STATUS_MESSAGES } from "@/enums/httpStatus"
import { useUserStore } from "@/stores/user"

const baseURL = import.meta.env.DEV ? "/api" : import.meta.env.VITE_BASE_URL
const timeout = 20000

/* 不具備錯誤處理的 axios instance（只具備對於訊息的處理） */
export const instance = axios.create({
    baseURL,
    timeout,
})

/* 補上 request body（payload）型別的 instance.request 包裝：
 * axios 的 request<T, R, D> 第二個泛型 R 預設就是 AxiosResponse<T>，
 * 這裡把它包起來，讓 api.ts 只需要指定 T（response）跟 D（payload）兩個泛型 */
export function request<TResponse, TPayload = unknown>(config: AxiosRequestConfig<TPayload>) {
    return instance.request<TResponse, AxiosResponse<TResponse>, TPayload>(config)
}

/* 固定 URL 的 endpoint：把「宣告 payload 型別」跟「組 request」合併成一次呼叫，
 * 不用像 request() 那樣呼叫端跟內部各寫一次泛型 */
export function defineRequest<TResponse, TPayload = unknown>(
    descriptor: AxiosRequestConfig & { url: string; method: Method }
) {
    return function (config: AxiosRequestConfig<TPayload> = {}) {
        return request<TResponse, TPayload>({ ...descriptor, ...config })
    }
}

/* 帶單一動態路徑參數（例如 /audiences/{id}/）的 endpoint：
 * url 改成接受該參數、回傳實際 URL 的函式，回傳的函式簽名也會多一個對應參數，
 * 忘記傳路徑參數會被 type-check 擋下來 */
export function defineRequestWithParam<TResponse, TPayload = unknown, TParam = string>(descriptor: {
    url: (param: TParam) => string
    method: Method
}) {
    return function (param: TParam, config: AxiosRequestConfig<TPayload> = {}) {
        return request<TResponse, TPayload>({ url: descriptor.url(param), method: descriptor.method, ...config })
    }
}

instance.interceptors.request.use(
    function (config) {
        if (config.skipAuth) return config

        const token = useUserStore().formatUserToken
        if (!token) return Promise.reject(new axios.CanceledError())
        config.headers["Authorization"] = config.headers["Authorization"] ?? `Bearer ${token}`
        return config
    },
    function (error) {
        throw error
    }
)

instance.interceptors.response.use(
    function (response) {
        response.config.metadata = {
            ...response.config.metadata,
            endTime: new Date(),
        }
        return response
    },

    async function (error) {
        const response = error?.response

        // 當錯誤為 401 並且 VITE_USE_REFRESH_TOKEN 為 true 時，會嘗試刷新重新登入
        if (response?.status === 401 && import.meta.env.VITE_USE_REFRESH_TOKEN === "true") {
            const refreshSuccess = await handleTokenRefresh()
            // 當重新登入一次成功，會重新開始請求｜當重新登入一次失敗，會顯示錯誤
            if (refreshSuccess) return restartRequest(error)
        }

        error.message = getCustomErrorMessage(response?.status, response?.data, error.code)

        error.status = error?.response?.status ?? "--"
        error.backendCode = error?.response?.data?.code ?? "--"

        throw error
    }
)

export function getCustomErrorMessage(
    status: number | undefined,
    responseData: { code?: number } | undefined,
    errorCode: string | undefined
): string {
    if (errorCode === "ECONNABORTED") return "網路請求超時，請重新嘗試"

    if (status === 400) {
        const code = responseData?.code
        return (code && customErrorCode[code]) || STATUS_MESSAGES[400]
    }

    if (status === undefined) return STATUS_MESSAGES.default
    return STATUS_MESSAGES[status] || STATUS_MESSAGES.default
}

async function handleTokenRefresh(): Promise<boolean> {
    const userStore = useUserStore()

    try {
        const response = await callRefreshToken({
            data: { refresh: userStore.formatUserRefreshToken },
        })
        userStore.setUserToken(response.data)
        return true
    } catch {
        await userStore.logout()
        return false
    }
}

async function restartRequest(error: AxiosError) {
    const userStore = useUserStore()
    const newToken = userStore.formatUserToken
    if (!newToken || !error.config) throw error
    error.config.headers["Authorization"] = `Bearer ${newToken}`
    return instance(error.config)
}
